import { useCallback, useEffect, useRef, useState } from 'react'
import { useProductsCatalogSearchParams } from './embeddedCatalogParams'
import { useQuery } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from 'supabase/database.types'
import { queryKeys } from 'src/api/db/queryKeys'
import { useProductsOpenApi } from 'src/api/db/apiProductsOpen'
import { ProductQueryResult } from './types'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, STALE_TIME_24_HOURS } from './consts'

export type BreadcrumbCategory = { id: number; title: string | null }

export const useBreadcrumbCategories = (categoryIds: number[]) => {
  const supabase = useSupabaseClient()

  const { data: categories } = useQuery({
    queryKey: [queryKeys.categories, 'breadcrumb', categoryIds],
    queryFn: async () => {
      if (categoryIds.length === 0) return []
      const { data, error } = await supabase
        .from('category')
        .select('id, title')
        .in('id', categoryIds)
      if (error) throw error
      const byId = new Map((data ?? []).map((c) => [c.id, c]))
      return categoryIds
        .map((id) => byId.get(id))
        .filter((c): c is NonNullable<typeof c> => c != null)
    },
    enabled: categoryIds.length > 0,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME_24_HOURS,
  })

  return categories ?? []
}

type SupabaseClientType = SupabaseClient<Database>

const sortByPopular = (
  productIds: number[],
  productMap: Map<
    number,
    { klayder_recommends: boolean; price: number | null }
  >,
  likeCountsMap: Map<number, number>
): number[] => {
  return productIds.sort((a, b) => {
    const aData = productMap.get(a)
    const bData = productMap.get(b)
    const aKlayder = aData?.klayder_recommends ?? false
    const bKlayder = bData?.klayder_recommends ?? false
    const aLikes = likeCountsMap.get(a) || 0
    const bLikes = likeCountsMap.get(b) || 0

    const aTier = aKlayder && aLikes > 0 ? 1 : aKlayder ? 2 : aLikes > 0 ? 3 : 4
    const bTier = bKlayder && bLikes > 0 ? 1 : bKlayder ? 2 : bLikes > 0 ? 3 : 4

    if (aTier !== bTier) return aTier - bTier
    if (aLikes !== bLikes) return bLikes - aLikes
    return 0
  })
}

export const getCategoryProductsQueryFn = async (
  supabase: SupabaseClientType,
  categoryId: number,
  page: number,
  pageSize: number
): Promise<ProductQueryResult> => {
  const from = (page - 1) * pageSize
  const to = from + pageSize

  const { data: allCategoryProducts, error } = await supabase
    .from('category_product')
    .select('product_id')
    .eq('category_id', categoryId)

  if (error) throw error

  let allProductIds = (allCategoryProducts || [])
    .map((cp) => cp.product_id)
    .filter((id): id is number => id !== null)

  if (allProductIds.length === 0) {
    return {
      data: [],
      count: 0,
      page,
      pageSize,
      totalPages: 0,
    }
  }

  // @ts-ignore - TODO1
  const { data: productsData } = await supabase
    // @ts-ignore - TODO1
    .from('product_open')
    .select('id, klayder_recommends, price')
    .in('id', allProductIds)
    .or('is_draft.is.null,is_draft.eq.false')
    .order('klayder_recommends', { ascending: false })

  const productMap = new Map<
    number,
    { klayder_recommends: boolean; price: number | null }
  >()
  productsData?.forEach((p: any) => {
    if (p.id !== null) {
      productMap.set(p.id, {
        klayder_recommends: p.klayder_recommends === true,
        price: p.price ?? null,
      })
    }
  })

  // Exclude drafts: only include products that appear in productMap (non-draft)
  allProductIds = allProductIds.filter((id) => productMap.has(id))
  const totalCount = allProductIds.length

  const { data: likesData } = await supabase
    .from('likes')
    .select('product_id')
    .in('product_id', allProductIds)
    .eq('like', true)

  const likeCountsMap = new Map<number, number>()
  likesData?.forEach((like) => {
    if (like.product_id !== null) {
      const currentCount = likeCountsMap.get(like.product_id) || 0
      likeCountsMap.set(like.product_id, currentCount + 1)
    }
  })

  const sortedProductIds = sortByPopular(
    allProductIds,
    productMap,
    likeCountsMap
  )

  const paginatedIds = sortedProductIds.slice(from, to)

  return {
    data: paginatedIds,
    count: totalCount,
    page,
    pageSize,
    totalPages: totalCount ? Math.ceil(totalCount / pageSize) : 0,
  }
}

const parsePageFromUrl = (param: string | null): number => {
  const parsed = parseInt(param || '', 10)
  return !isNaN(parsed) && parsed >= 1 ? parsed : DEFAULT_PAGE
}

export const useProductsPageData = (selectedCategoryId: number | undefined) => {
  const supabase = useSupabaseClient()
  const [searchParams, setSearchParams] = useProductsCatalogSearchParams()
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const page = parsePageFromUrl(searchParams.get('page'))

  const updatePageInUrl = useCallback(
    (newPage: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (newPage === DEFAULT_PAGE) {
            next.delete('page')
          } else {
            next.set('page', String(newPage))
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const { data: categoryProductsIds, isFetching } = useQuery({
    queryKey: [
      'queryKeys.categoryProducts',
      selectedCategoryId,
      page,
      pageSize,
    ],
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: STALE_TIME_24_HOURS,
    queryFn: () =>
      getCategoryProductsQueryFn(supabase, selectedCategoryId!, page, pageSize),
    enabled: !!selectedCategoryId,
  })

  const activeResult: ProductQueryResult | undefined = selectedCategoryId
    ? categoryProductsIds
    : undefined
  const productIds = activeResult?.data
  const product_items =
    productIds?.map((productId) => ({ product_id: productId })) || []
  const currentTotalPages = activeResult?.totalPages || 0

  useEffect(() => {
    if (!activeResult) return
    if (activeResult.count === 0 && page !== DEFAULT_PAGE) {
      updatePageInUrl(DEFAULT_PAGE)
      return
    }
    if (currentTotalPages > 0 && page > currentTotalPages) {
      updatePageInUrl(currentTotalPages)
    }
  }, [activeResult, currentTotalPages, page, updatePageInUrl])

  const prevCategoryRef = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (
      prevCategoryRef.current !== undefined &&
      prevCategoryRef.current !== selectedCategoryId
    ) {
      updatePageInUrl(DEFAULT_PAGE)
    }
    prevCategoryRef.current = selectedCategoryId
  }, [selectedCategoryId, updatePageInUrl])

  return {
    isLoading: isFetching,
    product_items,
    activeResult,
    page,
    pageSize,
    currentTotalPages,
    onPageChange: updatePageInUrl,
    onPageSizeChange: (newPageSize: number) => {
      setPageSize(newPageSize)
      updatePageInUrl(DEFAULT_PAGE)
    },
  }
}

export const useProductsSearchData = (searchTerm: string | null) => {
  const [searchParams, setSearchParams] = useProductsCatalogSearchParams()
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const page = parsePageFromUrl(searchParams.get('page'))

  const updatePageInUrl = useCallback(
    (newPage: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (newPage === DEFAULT_PAGE) {
            next.delete('page')
          } else {
            next.set('page', String(newPage))
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const { productsByIdsOpen, isLoadingProducts2 } = useProductsOpenApi({
    searchText: searchTerm ?? undefined,
    page,
    pageSize,
  })

  const product_items = (productsByIdsOpen?.data
    ?.filter((p): p is typeof p & { id: number } => p.id != null)
    ?.map((p) => ({ product_id: p.id }) as { product_id: number }) ?? []) as {
    product_id?: number
  }[]
  const currentTotalPages = productsByIdsOpen?.totalPages ?? 0

  const activeResult: ProductQueryResult | undefined = productsByIdsOpen
    ? {
        data: product_items.map((i) => i.product_id!).filter(Boolean),
        count: productsByIdsOpen.count ?? 0,
        page: productsByIdsOpen.page ?? page,
        pageSize: productsByIdsOpen.pageSize ?? pageSize,
        totalPages: productsByIdsOpen.totalPages ?? 0,
      }
    : undefined

  useEffect(() => {
    if (!activeResult) return
    if (activeResult.count === 0 && page !== DEFAULT_PAGE) {
      updatePageInUrl(DEFAULT_PAGE)
      return
    }
    if (currentTotalPages > 0 && page > currentTotalPages) {
      updatePageInUrl(currentTotalPages)
    }
  }, [activeResult, currentTotalPages, page, updatePageInUrl])

  const prevSearchRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (
      prevSearchRef.current !== undefined &&
      prevSearchRef.current !== searchTerm
    ) {
      updatePageInUrl(DEFAULT_PAGE)
    }
    prevSearchRef.current = searchTerm
  }, [searchTerm, updatePageInUrl])

  return {
    isLoading: isLoadingProducts2,
    product_items,
    activeResult,
    page,
    pageSize,
    currentTotalPages,
    onPageChange: updatePageInUrl,
    onPageSizeChange: (newPageSize: number) => {
      setPageSize(newPageSize)
      updatePageInUrl(DEFAULT_PAGE)
    },
  }
}
